<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Billing</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your free trial, active subscription, and Stripe checkout.
        </p>
      </div>
      <button
        v-if="status?.payment_link"
        @click="openPaymentLink"
        class="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
      >
        Continue payment
      </button>
    </div>

    <div v-if="notice" class="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
      {{ notice }}
    </div>
    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
      {{ error }}
    </div>

    <div class="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
      <section class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Account status</h2>
          <span
            class="rounded-full px-3 py-1 text-xs font-semibold"
            :class="statusBadgeClass"
          >
            {{ statusLabel }}
          </span>
        </div>

        <dl class="mt-6 space-y-4 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Plan</dt>
            <dd class="font-medium text-gray-900 dark:text-white">{{ status?.plan_name || status?.subscription_plan || 'Free trial' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Billing</dt>
            <dd class="font-medium text-gray-900 dark:text-white">{{ billingPeriodLabel }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Agents</dt>
            <dd class="font-medium text-gray-900 dark:text-white">{{ status?.current_agent_count || 0 }} / {{ status?.max_agents || 0 }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Trial ends</dt>
            <dd class="font-medium text-gray-900 dark:text-white">{{ formatDate(status?.trial_end_date) }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Renews</dt>
            <dd class="font-medium text-gray-900 dark:text-white">{{ formatDate(status?.billing_period_end) }}</dd>
          </div>
        </dl>

        <p class="mt-6 text-sm text-gray-500 dark:text-gray-400">
          Payments are handled by Stripe. After payment, Stripe sends the buyer a receipt using the billing email used during checkout.
        </p>
      </section>

      <section class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Choose plan</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Pay monthly or annually. Annual plans activate for 12 months.</p>
          </div>
          <div class="inline-flex rounded-md border border-gray-200 p-1 dark:border-gray-700">
            <button
              class="rounded px-3 py-1.5 text-sm font-semibold"
              :class="period === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300'"
              @click="period = 'monthly'"
            >
              Monthly
            </button>
            <button
              class="rounded px-3 py-1.5 text-sm font-semibold"
              :class="period === 'yearly' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300'"
              @click="period = 'yearly'"
            >
              Annual
            </button>
          </div>
        </div>

        <div v-if="loading" class="mt-8 text-sm text-gray-500 dark:text-gray-400">Loading billing...</div>
        <div v-else class="mt-6 grid gap-4 md:grid-cols-2">
          <article
            v-for="plan in visiblePlans"
            :key="plan.id"
            class="rounded-lg border border-gray-200 p-5 dark:border-gray-700"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ plan.name }}</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ plan.description }}</p>
              </div>
              <span v-if="plan.id === status?.subscription_plan_id" class="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">Current</span>
            </div>
            <div class="mt-5 text-3xl font-bold text-gray-900 dark:text-white">
              {{ plan.price_sek }} kr
              <span class="text-sm font-medium text-gray-500 dark:text-gray-400">/{{ plan.billing_period === 'yearly' ? 'year' : 'month' }}</span>
            </div>
            <ul class="mt-5 space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li v-for="feature in plan.features" :key="feature" class="flex gap-2">
                <span class="font-bold text-blue-600">✓</span>
                <span>{{ feature }}</span>
              </li>
              <li class="flex gap-2">
                <span class="font-bold text-blue-600">✓</span>
                <span>{{ plan.max_agents === 0 ? 'Unlimited agents' : `${plan.max_agents} agents` }}</span>
              </li>
            </ul>
            <button
              :disabled="checkoutLoading === plan.id"
              @click="startCheckout(plan.id)"
              class="mt-6 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {{ checkoutLoading === plan.id ? 'Opening Stripe...' : plan.price_sek === 0 ? 'Use free plan' : 'Pay with Stripe' }}
            </button>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { billingService, type BillingStatus, type SubscriptionPlan } from '../services/billing'

const loading = ref(true)
const checkoutLoading = ref<number | null>(null)
const status = ref<BillingStatus | null>(null)
const plans = ref<SubscriptionPlan[]>([])
const period = ref<'monthly' | 'yearly'>('monthly')
const error = ref('')
const notice = ref('')

const visiblePlans = computed(() => plans.value.filter((plan) => plan.billing_period === period.value))

const statusLabel = computed(() => {
  if (!status.value) return 'Loading'
  if (status.value.subscription_status === 'active' && status.value.billing_status === 'active') return 'Active'
  if (status.value.subscription_status === 'trial') return 'Free trial'
  if (status.value.billing_status === 'expiring_soon') return 'Expiring soon'
  if (status.value.billing_status === 'suspended') return 'Suspended'
  return 'Payment needed'
})

const statusBadgeClass = computed(() => {
  if (!status.value) return 'bg-gray-100 text-gray-700'
  if (status.value.subscription_status === 'active' && status.value.billing_status === 'active') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200'
  if (status.value.subscription_status === 'trial') return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200'
  return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200'
})

const billingPeriodLabel = computed(() => {
  if (!status.value?.billing_period) return 'Not selected'
  return status.value.billing_period === 'yearly' ? 'Annual' : 'Monthly'
})

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set'
  return new Date(value).toLocaleDateString()
}

const loadBilling = async () => {
  loading.value = true
  error.value = ''
  try {
    const [statusData, plansData] = await Promise.all([
      billingService.getStatus(),
      billingService.getPlans()
    ])
    status.value = statusData
    plans.value = plansData
    if (statusData.billing_period === 'yearly') period.value = 'yearly'
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load billing'
  } finally {
    loading.value = false
  }
}

const confirmReturnedPayment = async () => {
  const query = new URLSearchParams(window.location.search)
  const payment = query.get('payment')
  const sessionId = query.get('session_id')
  if (payment === 'cancelled') {
    notice.value = 'Checkout was cancelled. Your account was not changed.'
    window.history.replaceState({}, document.title, window.location.pathname)
    return
  }
  if (payment === 'free') {
    notice.value = 'Free plan activated.'
    window.history.replaceState({}, document.title, window.location.pathname)
    return
  }
  if (payment === 'success' && sessionId) {
    try {
      await billingService.confirmCheckout(sessionId)
      notice.value = 'Payment confirmed. Your subscription is active.'
      window.history.replaceState({}, document.title, window.location.pathname)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Could not confirm payment'
    }
  }
}

const startCheckout = async (planId: number) => {
  checkoutLoading.value = planId
  error.value = ''
  try {
    const url = await billingService.createCheckout(planId)
    window.location.href = url
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Could not start checkout'
  } finally {
    checkoutLoading.value = null
  }
}

const openPaymentLink = () => {
  if (status.value?.payment_link) window.location.href = status.value.payment_link
}

onMounted(async () => {
  await confirmReturnedPayment()
  await loadBilling()
})
</script>
